import { useCallback, useEffect, useRef, useState } from "react";
import { createClient as createWSClient, Client } from "graphql-ws";
import { request } from "graphql-request";
import { gqlServerURL, gqlWsURL } from "@/config";
import {
  AGENT_CHAT_STREAM_SUBSCRIPTION,
  AGENT_CHAT_STREAM_REPLAY_QUERY,
} from "@/graphql/chat-requests";

// Wire shape of a stream event as the backend delivers it (both live
// via subscription and buffered via replay). Kept loose on `metadata`
// because the backend smuggles the original runConversation event in
// there and consumers read different fields per event type.
//
// `seq` is the per-stream monotonic sequence number assigned by the
// backend StreamRegistry. The hook tracks the highest seq it has
// forwarded to the consumer per streamId so the replay+subscription
// hand-off cannot deliver the same event twice.
export type AgentStreamEvent = {
  type: string;
  content: string;
  metadata?: Record<string, unknown> | null;
  timestamp?: string;
  streamId?: string;
  seq?: number;
};

export type StreamConnectionState =
  | "idle"
  | "connecting"
  | "open"
  | "retrying"
  | "closed";

// Callbacks the consumer provides. `onEvent` is called for every event
// — both buffered replay events and live subscription events — in
// publish order, so the consumer's existing per-event-type handlers
// don't need to know which is which. `onError` and `onComplete` mirror
// the underlying graphql-ws sink. We also expose a `source` flag so
// consumers that care (e.g. to suppress a "thread_renamed" side-effect
// during replay) can distinguish, but most won't.
export type StreamHandlers = {
  onEvent: (
    event: AgentStreamEvent,
    source: "replay" | "live"
  ) => void;
  onError?: (err: unknown) => void;
  onComplete?: () => void;
};

type SubscribeOptions = {
  // If true, fetch the buffered events via agentChatStreamReplay first
  // and feed them through onEvent before opening the live subscription.
  // Default true. Pass false when starting a brand-new stream where the
  // buffer is guaranteed empty.
  replayFirst?: boolean;
};

type UseAgentChatStreamArgs = {
  accessToken?: string;
};

export type AgentChatStreamHook = {
  connectionState: StreamConnectionState;
  subscribe: (
    streamId: string,
    handlers: StreamHandlers,
    options?: SubscribeOptions
  ) => () => void;
  rejoin: (streamId: string, handlers: StreamHandlers) => () => void;
  isSubscribedTo: (streamId: string) => boolean;
};

/**
 * Owns the WebSocket connection to the agent-chat subscription endpoint
 * and exposes a small API for subscribing to / replaying a given
 * streamId. Designed so the consumer's per-event-type handlers (text
 * delta, tool call, done, …) live in one place and run for both replay
 * and live events.
 *
 * Why a hook (and not just call createWSClient inline like before):
 *   - Auto-reconnect is configured here (retryAttempts: Infinity); the
 *     old inline setup didn't pass retry options, so a transient drop
 *     left the chat permanently broken until refresh.
 *   - `rejoin(streamId, handlers)` is the missing piece that fixes the
 *     mid-stream rejoin bug: it queries the buffered events first, then
 *     opens the subscription for the tail.
 *   - `isSubscribedTo(streamId)` lets the consumer avoid double-
 *     subscribing if a render path tries to rejoin a stream we're
 *     already attached to.
 */
export function useAgentChatStream({
  accessToken,
}: UseAgentChatStreamArgs): AgentChatStreamHook {
  const clientRef = useRef<Client | null>(null);
  const lastTokenRef = useRef<string | undefined>(undefined);
  // Tracks active subscriptions by streamId → dispose function. Used by
  // isSubscribedTo and by rejoin to avoid duplicates.
  const activeSubsRef = useRef<Map<string, () => void>>(new Map());
  // Per-streamId high-water-mark of seq numbers already forwarded to
  // the consumer. The backend assigns 1,2,3,... per stream. The
  // replay query returns events 1..N, then the subscription delivers
  // events that may overlap (live publish raced the replay query).
  // Events with seq <= the recorded mark are dropped before reaching
  // the consumer's onEvent — this is the single source of truth for
  // "we don't double-render anything".
  const lastSeenSeqRef = useRef<Map<string, number>>(new Map());
  // Handlers registry keyed by streamId. We hold onto the consumer's
  // onEvent/onError/onComplete so that when the WebSocket reconnects
  // after a drop we can call the replay query and pipe missed events
  // back through the same handler — keeping the streaming bubble alive
  // through transient network blips. graphql-ws auto-resubscribes the
  // subscription itself; replay closes the "events published while we
  // were disconnected" gap that the auto-resubscribe alone leaves.
  const handlerRegistryRef = useRef<Map<string, StreamHandlers>>(new Map());
  // First-connect flag so the very first `connected` event doesn't
  // trigger a no-op replay (no subscriptions exist yet on cold start).
  const isFirstConnectRef = useRef<boolean>(true);
  const [connectionState, setConnectionState] =
    useState<StreamConnectionState>("idle");

  // Internal replay helper used by both the public replay() method and
  // by the reconnect handler in on.connected. Pipes every buffered
  // event through the per-stream lastSeenSeq dedupe gate, so callers
  // can invoke it any number of times without producing duplicates.
  const doReplayInternal = useCallback(
    async (streamId: string, handlers: StreamHandlers): Promise<void> => {
      const headers = (accessToken || lastTokenRef.current)
        ? { Authorization: accessToken || lastTokenRef.current! }
        : undefined;
      try {
        const resp = await (request as unknown as (
          url: string,
          query: string,
          variables: Record<string, unknown>,
          headers?: Record<string, string>
        ) => Promise<{
          agentChatStreamReplay: {
            streamId: string;
            status: string;
            events: AgentStreamEvent[];
          };
        }>)(
          gqlServerURL + "/graphql",
          AGENT_CHAT_STREAM_REPLAY_QUERY,
          { streamId },
          headers as Record<string, string> | undefined
        );
        const r = resp?.agentChatStreamReplay;
        if (r && Array.isArray(r.events)) {
          for (const ev of r.events) {
            const seq = typeof ev.seq === "number" ? ev.seq : 0;
            if (seq > 0) {
              const seen = lastSeenSeqRef.current.get(streamId) ?? 0;
              if (seq <= seen) continue;
              lastSeenSeqRef.current.set(streamId, seq);
            }
            handlers.onEvent(ev, "replay");
          }
        }
      } catch (err) {
        handlers.onError?.(err);
      }
    },
    [accessToken]
  );

  // (Re)build the WebSocket client whenever the access token changes.
  // graphql-ws's retryAttempts: Infinity + shouldRetry: () => true
  // means transient network drops are recovered automatically without
  // tearing down active subscriptions.
  useEffect(() => {
    const token =
      accessToken || (window as { __TD_ACCESS_TOKEN__?: string }).__TD_ACCESS_TOKEN__ || undefined;

    if (clientRef.current && lastTokenRef.current === token) return;

    // Dispose any previous client so the new connection uses fresh
    // auth. Active subscriptions will surface as errors on the old
    // sinks; consumers should re-subscribe on token rotation.
    if (clientRef.current) {
      try {
        (clientRef.current as { dispose?: () => void }).dispose?.();
      } catch {
        // ignore
      }
      clientRef.current = null;
      activeSubsRef.current.clear();
    }

    lastTokenRef.current = token;
    isFirstConnectRef.current = true;
    setConnectionState("connecting");
    clientRef.current = createWSClient({
      url: gqlWsURL,
      connectionParams: token
        ? {
            Authorization: token,
            authorization: token,
            headers: { authorization: token },
          }
        : undefined,
      // Infinite retries with a small jitter — the server-side
      // heartbeat will terminate any silently-dead socket within ~60s
      // so we don't sit on a phantom connection.
      retryAttempts: Infinity,
      shouldRetry: () => true,
      retryWait: async (retries: number) => {
        const base = Math.min(30_000, 1_000 * Math.pow(2, retries));
        const jitter = Math.random() * 500;
        await new Promise((res) => setTimeout(res, base + jitter));
      },
      on: {
        connected: () => {
          setConnectionState("open");
          // eslint-disable-next-line no-console
          console.log("[WS] Connected to GraphQL subscriptions at", gqlWsURL);
          // First connect: nothing to replay (no subscriptions exist).
          // Subsequent connects (reconnects after a drop): walk the
          // handler registry and replay every active stream so events
          // that the server published while we were disconnected get
          // delivered. graphql-ws auto-resubscribes the subscription
          // itself, but the new subscription only sees FUTURE events
          // — replay closes that gap. dedupe via lastSeenSeq makes it
          // safe to run even if no events were actually missed.
          if (isFirstConnectRef.current) {
            isFirstConnectRef.current = false;
            return;
          }
          const entries = Array.from(handlerRegistryRef.current.entries());
          if (entries.length === 0) return;
          // eslint-disable-next-line no-console
          console.log(`[WS] Reconnected — replaying ${entries.length} stream(s)`);
          for (const [streamId, handlers] of entries) {
            // Fire-and-forget; the helper handles its own errors.
            void doReplayInternal(streamId, handlers);
          }
        },
        closed: (ev: unknown) => {
          setConnectionState("closed");
          // eslint-disable-next-line no-console
          console.log("[WS] Closed", (ev as { code?: number })?.code, (ev as { reason?: string })?.reason);
        },
        error: () => {
          setConnectionState("retrying");
        },
      },
    });

    return () => {
      if (clientRef.current) {
        try {
          (clientRef.current as { dispose?: () => void }).dispose?.();
        } catch {
          // ignore
        }
        clientRef.current = null;
      }
      activeSubsRef.current.clear();
    };
  }, [accessToken]);

  const subscribe = useCallback(
    (
      streamId: string,
      handlers: StreamHandlers,
      options?: SubscribeOptions
    ): (() => void) => {
      const client = clientRef.current;
      if (!client) {
        // Defensive — should never happen because the effect above runs
        // before any caller can hit this, but surface a clear error so
        // bugs are obvious rather than silent.
        handlers.onError?.(new Error("WebSocket client not ready"));
        return () => undefined;
      }

      // If we're already subscribed to this streamId, tear down the
      // existing subscription before opening a new one — the new
      // handlers are what the caller wants now.
      const existing = activeSubsRef.current.get(streamId);
      if (existing) {
        try {
          existing();
        } catch {
          // ignore
        }
        activeSubsRef.current.delete(streamId);
      }

      const replayFirst = options?.replayFirst !== false;

      // Track whether the consumer has torn down (via dispose) so an
      // in-flight replay HTTP request doesn't fire onEvent after.
      let disposed = false;

      // Single dedupe gate for every event before it reaches the
      // consumer. Drops anything with seq <= the per-stream
      // high-water-mark; updates the mark on accept. Sentinel events
      // (seq === 0, e.g. backend termination markers) bypass the gate
      // since they carry no payload identity.
      const deliver = (
        e: AgentStreamEvent,
        source: "replay" | "live"
      ): void => {
        if (disposed) return;
        const seq = typeof e.seq === "number" ? e.seq : 0;
        if (seq > 0) {
          const seen = lastSeenSeqRef.current.get(streamId) ?? 0;
          if (seq <= seen) return;
          lastSeenSeqRef.current.set(streamId, seq);
        }
        handlers.onEvent(e, source);
      };

      const openLive = () => {
        if (disposed) return;
        const dispose = (
          client as unknown as {
            subscribe: (
              payload: { query: string; variables: Record<string, unknown> },
              sink: {
                next: (value: unknown) => void;
                error: (err: unknown) => void;
                complete: () => void;
              }
            ) => () => void;
          }
        ).subscribe(
          {
            query: AGENT_CHAT_STREAM_SUBSCRIPTION,
            variables: { streamId },
          },
          {
            next: (value: unknown) => {
              const ev = (value as { data?: { agentChatStream?: AgentStreamEvent } })
                ?.data?.agentChatStream;
              if (ev) deliver(ev, "live");
            },
            error: (err: unknown) => {
              handlers.onError?.(err);
            },
            complete: () => {
              handlers.onComplete?.();
            },
          }
        );

        // Wrap so removing from activeSubsRef is part of dispose.
        const wrappedDispose = () => {
          disposed = true;
          try {
            dispose();
          } catch {
            // ignore
          }
          activeSubsRef.current.delete(streamId);
          // Match the outer subscribe() dispose semantics so callers
          // that get this function via `rejoin()` returning an
          // existing subscription still trigger the full cleanup.
          // Without these two deletes, an `existing`-return dispose
          // leaves the handler in handlerRegistryRef; on the next WS
          // reconnect the reconnect handler walks the registry and
          // re-replays the finished stream into whatever consumer is
          // currently mounted — bleeding chat A's `done`/`finalize`
          // events into chat B's live UI.
          lastSeenSeqRef.current.delete(streamId);
          handlerRegistryRef.current.delete(streamId);
        };
        activeSubsRef.current.set(streamId, wrappedDispose);
      };

      // Register handlers for reconnect-replay. When the WS reconnects
      // after a transient drop, the on.connected callback walks this
      // registry and re-issues replay queries for every active stream,
      // so events that the server published while we were disconnected
      // (and that graphql-ws's auto-resubscribe missed because the
      // subscription started "now") get delivered. Dedupe via
      // lastSeenSeq prevents the consumer from seeing duplicates.
      handlerRegistryRef.current.set(streamId, handlers);

      if (replayFirst) {
        // Fire-and-forget the replay; on completion (or failure) open
        // the live subscription. Buffer the live events that arrive
        // while replay is in flight via a queue, then drain them in
        // order so the consumer sees a clean timeline. The backend's
        // registry subscription resolver already gives us this
        // ordering when graphql-ws drives the iterator directly — but
        // since we want the consumer to see replay events even when
        // the buffer was evicted server-side ('unknown' status), we
        // run the explicit replay query path here.
        const headers = (accessToken || lastTokenRef.current)
          ? { Authorization: accessToken || lastTokenRef.current! }
          : undefined;
        (async () => {
          try {
            const resp = await (request as unknown as (
              url: string,
              query: string,
              variables: Record<string, unknown>,
              headers?: Record<string, string>
            ) => Promise<{
              agentChatStreamReplay: {
                streamId: string;
                status: string;
                events: AgentStreamEvent[];
              };
            }>)(
              gqlServerURL + "/graphql",
              AGENT_CHAT_STREAM_REPLAY_QUERY,
              { streamId },
              headers as Record<string, string> | undefined
            );
            const replay = resp?.agentChatStreamReplay;
            if (disposed) return;
            if (replay && Array.isArray(replay.events)) {
              for (const ev of replay.events) {
                if (disposed) return;
                deliver(ev, "replay");
              }
            }
            // Only open a live subscription if the stream is still
            // running. If status is 'done' or 'error', the registry
            // has the final event list and a subscription would only
            // tell us the topic is closed.
            if (!replay || replay.status === "running") {
              openLive();
              // Gap-fill: events the server published between
              // snapshotting the replay response and openLive()
              // registering the subscription were routed through
              // PubSub before any subscriber existed and were
              // dropped. Re-issue the replay so the deliver gate
              // (lastSeenSeq) hands us only the gap — critically
              // including any `done` event that landed in the
              // window, which would otherwise leave the UI stuck on
              // "AI thinking" until the 30s watchdog refetches.
              //
              // Routes events through `deliver` (not the hook-level
              // doReplayInternal) so the closure's `disposed` flag
              // gates both the launch and every delivered event.
              // Without that gate, a dispose() during the HTTP
              // round-trip would clear lastSeenSeq and let every
              // gap-fill event re-fire on the stale handler set —
              // leaking Chat A's `finalize`/`done` into whatever
              // chat the user navigated to.
              if (!disposed) {
                void (async () => {
                  try {
                    const gapResp = await (request as unknown as (
                      url: string,
                      query: string,
                      variables: Record<string, unknown>,
                      headers?: Record<string, string>
                    ) => Promise<{
                      agentChatStreamReplay: {
                        streamId: string;
                        status: string;
                        events: AgentStreamEvent[];
                      };
                    }>)(
                      gqlServerURL + "/graphql",
                      AGENT_CHAT_STREAM_REPLAY_QUERY,
                      { streamId },
                      headers as Record<string, string> | undefined
                    );
                    if (disposed) return;
                    const gap = gapResp?.agentChatStreamReplay;
                    if (gap && Array.isArray(gap.events)) {
                      for (const ev of gap.events) {
                        if (disposed) return;
                        deliver(ev, "replay");
                      }
                    }
                  } catch (err) {
                    if (disposed) return;
                    handlers.onError?.(err);
                  }
                })();
              }
            } else if (replay.status === "done" || replay.status === "error") {
              handlers.onComplete?.();
            } else {
              // 'unknown' — registry has no entry for this streamId.
              // Either the buffer was evicted (>120s after a clean
              // end) or the API crashed mid-generation without
              // clearing chat.activeStreamId. The PubSub topic
              // doesn't exist, so opening a live subscription would
              // sit idle forever. Synthesize a `done` replay event
              // so the consumer's replay-done handler clears the
              // live buffers / showAIThinking, then signal
              // completion to close the iterator semantics.
              handlers.onEvent(
                {
                  type: "done",
                  content: "",
                  metadata: { reason: "stream_unknown" },
                  timestamp: new Date().toISOString(),
                  streamId,
                  seq: 0,
                },
                "replay"
              );
              handlers.onComplete?.();
            }
          } catch (err) {
            // Replay failure shouldn't block the live path — open the
            // subscription anyway so the consumer at least sees tail
            // events. The error is surfaced for diagnostics.
            if (disposed) return;
            handlers.onError?.(err);
            openLive();
          }
        })();
      } else {
        openLive();
      }

      return () => {
        disposed = true;
        const sub = activeSubsRef.current.get(streamId);
        if (sub) {
          try {
            sub();
          } catch {
            // ignore
          }
          activeSubsRef.current.delete(streamId);
        }
        // Forget the dedupe mark for this stream — the consumer
        // explicitly tore down. A subsequent rejoin should replay all
        // events from seq 1 as if it were a fresh attach.
        lastSeenSeqRef.current.delete(streamId);
        handlerRegistryRef.current.delete(streamId);
      };
    },
    [accessToken]
  );

  const rejoin = useCallback(
    (streamId: string, handlers: StreamHandlers): (() => void) => {
      // Idempotent: if we already have a live subscription for this
      // streamId, do nothing and return its existing dispose.
      const existing = activeSubsRef.current.get(streamId);
      if (existing) return existing;
      return subscribe(streamId, handlers, { replayFirst: true });
    },
    [subscribe]
  );

  const isSubscribedTo = useCallback(
    (streamId: string): boolean => activeSubsRef.current.has(streamId),
    []
  );

  return { connectionState, subscribe, rejoin, isSubscribedTo };
}
